import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Search, 
  Target, 
  Building, 
  Users, 
  Globe, 
  DollarSign,
  TrendingUp,
  Sparkles,
  CheckCircle,
  AlertCircle,
  Loader,
  ExternalLink,
  MapPin,
  Calendar,
  Phone,
  Mail,
  Star,
  Save,
  X,
  CheckSquare,
  Square
} from 'lucide-react';
import { api } from '../../utils/api';

const LeadGen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Form states
  const [searchCriteria, setSearchCriteria] = useState({
    criteria: '', // Natural language search criteria
    region: 'th', // Default to Thailand
    language: 'th' // Default to Thai
  });

  const [maxLeads, setMaxLeads] = useState(10);
  
  // Data source selection
  const [selectedSources, setSelectedSources] = useState({
    // API-based sources (Official APIs)
    'govspending-api': true,  // GovSpending API (Official)
    'dbd': false,              // DBD API (if available)
    // Web search sources
    'tavily': true,            // General Tavily search
    'linkedin': true,          // LinkedIn profiles
    'websites': true,          // Thai websites (.co.th)
    'b2b': true,               // B2B Marketplaces
    'public-directories': true // Public business directories
  });

  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedLeads, setGeneratedLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [hoveredLeadScore, setHoveredLeadScore] = useState(null);
  const [processingStats, setProcessingStats] = useState(null);
  
  // Source filtering
  const [selectedSource, setSelectedSource] = useState('all'); // 'all', 'govspending-api', 'govspending', 'dbd', etc.
  const [sourceStats, setSourceStats] = useState({});
  
  // Modal states
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Duplicate customer check states
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateCustomers, setDuplicateCustomers] = useState([]);
  const [pendingLead, setPendingLead] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const handleCriteriaChange = (field, value) => {
    setSearchCriteria(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle lead selection
  const handleLeadSelect = (lead) => {
    setSelectedLead(lead);
    setShowLeadModal(true);
  };

  // Check for duplicate customers by company name
  const checkForDuplicateCustomers = async (companyName) => {
    if (!companyName || !companyName.trim()) {
      return [];
    }

    try {
      const response = await api.get(`/customers/search-by-company?companyName=${encodeURIComponent(companyName.trim())}`);
      if (response.success && response.customers) {
        return response.customers;
      }
      return [];
    } catch (error) {
      console.warn('Failed to check for duplicate customers:', error);
      return [];
    }
  };

  // Generate smart lead title
  const generateLeadTitle = (lead) => {
    // Maximum title length
    const MAX_TITLE_LENGTH = 80;
    
    // Generic terms to exclude from contact names (Thai and English)
    const genericContactTerms = [
      'contact', 'contacts', 'contact us', 'contactus',
      'ติดต่อ', 'ติดต่อเรา', 'ติดต่อ เรา',
      'unknown', 'unknown contact', 'n/a', 'na',
      'admin', 'administrator', 'webmaster',
      'info', 'information', 'support',
      'sales', 'general', 'inquiry'
    ];
    
    // Helper function to check if text is a generic contact term
    const isGenericContactTerm = (text) => {
      if (!text || typeof text !== 'string') return true;
      const normalized = text.toLowerCase().trim();
      return genericContactTerms.some(term => normalized === term || normalized.includes(term));
    };
    
    // Helper function to validate company name
    const isValidCompanyName = (name) => {
      if (!name || typeof name !== 'string') return false;
      const trimmed = name.trim();
      if (!trimmed || trimmed === 'Unknown Company') return false;
      return !isGenericContactTerm(trimmed);
    };
    
    // Helper function to validate contact name
    const isValidContactName = (name) => {
      if (!name || typeof name !== 'string') return false;
      const trimmed = name.trim();
      if (!trimmed) return false;
      return !isGenericContactTerm(trimmed);
    };
    
    // PRIORITY 0: Use "Gov Contractor:" + Company Name for GovSpending leads (HIGHEST PRIORITY)
    // Check if this is a GovSpending lead by checking source
    const isGovSpendingLead = lead.source && (
      lead.source.includes('GovSpending API') || 
      lead.source.includes('จัดซื้อจัดจ้างภาครัฐ (GovSpending API)') ||
      lead.source.includes('GovSpending API - Company') ||
      lead.source.includes('Thailand GovSpending')
    );
    
    if (isGovSpendingLead && isValidCompanyName(lead.CompanyName)) {
      let title = `Gov Contractor: ${lead.CompanyName.trim()}`;
      
      // Truncate if too long
      if (title.length > MAX_TITLE_LENGTH) {
        title = title.substring(0, MAX_TITLE_LENGTH - 3) + '...';
      }
      
      return title;
    }
    
    // Priority 1: Use Company Name + Industry/Job Title for context
    if (isValidCompanyName(lead.CompanyName)) {
      let title = lead.CompanyName.trim();
      
      // Add context (Industry or Job Title)
      if (lead.Industry && lead.Industry.trim()) {
        title = `${title} - ${lead.Industry.trim()}`;
      } else if (lead.JobTitle && lead.JobTitle.trim() && !isGenericContactTerm(lead.JobTitle)) {
        title = `${title} - ${lead.JobTitle.trim()}`;
      }
      
      // Truncate if too long
      if (title.length > MAX_TITLE_LENGTH) {
        title = title.substring(0, MAX_TITLE_LENGTH - 3) + '...';
      }
      
      return title;
    }
    
    // Priority 2: Use Contact Name + Job Title (only if not generic)
    if (isValidContactName(lead.ContactName)) {
      let title = lead.ContactName.trim();
      
      if (lead.JobTitle && lead.JobTitle.trim() && !isGenericContactTerm(lead.JobTitle)) {
        title = `${title} - ${lead.JobTitle.trim()}`;
      } else {
        title = `${title} - Lead Contact`;
      }
      
      // Truncate if too long
      if (title.length > MAX_TITLE_LENGTH) {
        title = title.substring(0, MAX_TITLE_LENGTH - 3) + '...';
      }
      
      return title;
    }
    
    // Priority 3: Use Industry + Source
    if (lead.Industry && lead.Industry.trim()) {
      const source = lead.source || 'AI Generated';
      let sourceLabel = 'Lead';
      
      if (source.includes('GovSpending')) {
        sourceLabel = 'GovSpending Lead';
      } else if (source.includes('DBD')) {
        sourceLabel = 'DBD Lead';
      } else if (source.includes('LinkedIn')) {
        sourceLabel = 'LinkedIn Lead';
      }
      
      return `${lead.Industry.trim()} - ${sourceLabel}`;
    }
    
    // Priority 4: Use Source + Location
    if (lead.Location && lead.Location.trim()) {
      const source = lead.source || 'AI Generated';
      let sourceLabel = 'Business';
      
      if (source.includes('GovSpending')) {
        sourceLabel = 'Government Contract';
      } else if (source.includes('DBD')) {
        sourceLabel = 'Registered Business';
      }
      
      return `${sourceLabel} in ${lead.Location.trim()}`;
    }
    
    // Fallback: Use source with timestamp
    const source = lead.source || 'AI Generated';
    const timestamp = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    
    if (source.includes('GovSpending')) {
      return `Government Contract Lead - ${timestamp}`;
    } else if (source.includes('DBD')) {
      return `Thai Business Lead - ${timestamp}`;
    } else if (source.includes('LinkedIn')) {
      return `LinkedIn Lead - ${timestamp}`;
    }
    
    return `New Lead - ${timestamp}`;
  };

  // Content enhancement function to clean and improve descriptions
  const enhanceContent = (content) => {
    if (!content || typeof content !== 'string') return content;
    
    let enhanced = content;
    
    // Remove HTML tags
    enhanced = enhanced.replace(/<[^>]*>/g, '');
    
    // Decode HTML entities
    enhanced = enhanced.replace(/&amp;/g, '&');
    enhanced = enhanced.replace(/&lt;/g, '<');
    enhanced = enhanced.replace(/&gt;/g, '>');
    enhanced = enhanced.replace(/&quot;/g, '"');
    enhanced = enhanced.replace(/&#39;/g, "'");
    enhanced = enhanced.replace(/&nbsp;/g, ' ');
    
    // Remove excessive whitespace and line breaks
    enhanced = enhanced.replace(/\s+/g, ' ');
    enhanced = enhanced.replace(/\n\s*\n/g, '\n');
    
    // Remove meaningless characters and symbols
    enhanced = enhanced.replace(/[^\w\s\u0E00-\u0E7F\u4E00-\u9FFF.,!?;:()\-'"]/g, ' ');
    
    // Clean up multiple spaces
    enhanced = enhanced.replace(/\s+/g, ' ').trim();
    
    // Capitalize first letter of sentences
    enhanced = enhanced.replace(/(^|[.!?]\s+)([a-z\u0E00-\u0E7F\u4E00-\u9FFF])/g, (match, p1, p2) => p1 + p2.toUpperCase());
    
    // Ensure proper sentence endings
    if (enhanced && !enhanced.match(/[.!?]$/)) {
      enhanced += '.';
    }
    
    return enhanced;
  };

  // Enhanced lead processing function
  const processLeadForSaving = (lead) => {
    const processedLead = { ...lead };
    
    // Generic terms to exclude
    const genericContactTerms = [
      'contact', 'contacts', 'contact us', 'contactus',
      'ติดต่อ', 'ติดต่อเรา', 'ติดต่อ เรา',
      'unknown', 'unknown contact'
    ];
    
    const isGenericTerm = (text) => {
      if (!text || typeof text !== 'string') return true;
      const normalized = text.toLowerCase().trim();
      return genericContactTerms.some(term => normalized === term || normalized.includes(term));
    };
    
    // Enhance description
    if (processedLead.Description) {
      processedLead.Description = enhanceContent(processedLead.Description);
      
      // If ContactName is generic, try to extract company name from description
      if (isGenericTerm(processedLead.ContactName) && processedLead.Description) {
        // Try to extract company name from Thai description patterns
        const thaiCompanyPatterns = [
          /บริษัท\s+([^\s]+(?:\s+[^\s]+){0,3})/i,
          /ห้างหุ้นส่วน\s+([^\s]+(?:\s+[^\s]+){0,3})/i,
          /([^\s]+(?:\s+[^\s]+){0,2})\s+จำกัด/i
        ];
        
        for (const pattern of thaiCompanyPatterns) {
          const match = processedLead.Description.match(pattern);
          if (match && match[0]) {
            if (!processedLead.CompanyName || processedLead.CompanyName === 'Unknown Company') {
              processedLead.CompanyName = match[0].trim();
              console.log('📝 Extracted company name from description:', processedLead.CompanyName);
            }
            break;
          }
        }
      }
    }
    
    // Enhance notes if exists
    if (processedLead.Notes) {
      processedLead.Notes = enhanceContent(processedLead.Notes);
    }
    
    // Clean up contact information
    if (processedLead.ContactEmail) {
      processedLead.ContactEmail = processedLead.ContactEmail.trim().toLowerCase();
    }
    
    if (processedLead.ContactPhone) {
      processedLead.ContactPhone = processedLead.ContactPhone.replace(/[^\d+\-\s()]/g, '').trim();
    }
    
    // Clean up company and contact names
    if (processedLead.CompanyName) {
      processedLead.CompanyName = enhanceContent(processedLead.CompanyName);
    }
    
    // If ContactName is generic, clear it or replace with company name
    if (processedLead.ContactName) {
      processedLead.ContactName = enhanceContent(processedLead.ContactName);
      if (isGenericTerm(processedLead.ContactName)) {
        // Replace generic contact name with company name if available
        if (processedLead.CompanyName && processedLead.CompanyName !== 'Unknown Company') {
          console.log('🔄 Replacing generic contact name with company name');
          processedLead.ContactName = processedLead.CompanyName;
        }
      }
    }
    
    if (processedLead.JobTitle) {
      processedLead.JobTitle = enhanceContent(processedLead.JobTitle);
    }
    
    return processedLead;
  };

  // Handle individual lead save
  const handleSaveLead = async (lead) => {
    // Check for duplicate customers first
    if (lead.CompanyName && lead.CompanyName.trim()) {
      const duplicates = await checkForDuplicateCustomers(lead.CompanyName);
      if (duplicates && duplicates.length > 0) {
        // Show duplicate modal
        setDuplicateCustomers(duplicates);
        setPendingLead(lead);
        setSelectedCustomer(null);
        setShowDuplicateModal(true);
        return; // Wait for user decision
      }
    }
    
    // No duplicates found, proceed with saving
    await saveLeadAfterDuplicateCheck(lead, null);
  };

  // Save lead after duplicate check (with optional customer link)
  const saveLeadAfterDuplicateCheck = async (lead, linkedCustomerId) => {
    setIsSaving(true);
    try {
      // Process and enhance the lead content before saving
      const processedLead = processLeadForSaving(lead);
      
      console.log('🔍 Original lead description:', lead.Description);
      console.log('✨ Enhanced lead description:', processedLead.Description);
      
      // Generate smart title
      if (!processedLead.Title || !processedLead.Title.trim()) {
        processedLead.Title = generateLeadTitle(lead);
      }
      
      console.log('📝 Generated lead title:', processedLead.Title);

      // First, save the lead with standardized source
      const leadToSave = {
        ...processedLead,
        LeadSource: 'Auto Generated', // Standardized source for all LeadGen leads
        Status: 'New',
        Priority: 'Medium',
        Score: lead.Score || lead.aiScore || 75,
        LinkedCustomerID: linkedCustomerId // Add customer link if provided
      };
      
      console.log('💾 Saving lead with standardized source: Auto Generated');
      if (linkedCustomerId) {
        console.log('🔗 Linking to customer:', linkedCustomerId);
      }
      
      const response = await api.post('/leads', leadToSave);
      if (response.leadId || response.message) {
        // If the lead has a source URL, save it as an attachment
        if (lead.LinkedIn && lead.LinkedIn.trim()) {
          try {
            await api.post(`/attachments/Lead/${response.leadId}/url`, {
              url: lead.LinkedIn,
              title: 'Source URL',
              description: `Source URL for lead: ${lead.ContactName} at ${lead.CompanyName}`,
              tags: 'source,url,lead-generation'
            });
            console.log('✅ Source URL saved as attachment');
          } catch (attachmentError) {
            console.warn('⚠️ Failed to save source URL as attachment:', attachmentError);
            // Don't fail the entire operation if attachment fails
          }
        }
        
        // Remove the saved lead from generated leads
        setGeneratedLeads(prev => prev.filter(l => l !== lead));
        setShowLeadModal(false);
        setSelectedLead(null);
        
        // Show success message
        alert('Lead saved successfully!');
      }
    } catch (error) {
      console.error('Error saving lead:', error);
      alert('Error saving lead. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle bulk lead save
  const handleBulkSave = async () => {
    if (selectedLeads.length === 0) {
      alert('Please select leads to save');
      return;
    }

    setIsSaving(true);
    try {
      // Process and enhance all selected leads before saving
      const processedLeads = selectedLeads.map(lead => {
        const pl = processLeadForSaving(lead);
        if (!pl.Title || !pl.Title.trim()) {
          pl.Title = generateLeadTitle(lead);
        }
        console.log('📝 Generated lead title:', pl.Title);
        return pl;
      });
      
      console.log('🔍 Processing', selectedLeads.length, 'leads for content enhancement...');
      
      const response = await api.post('/leads/bulk', { leads: processedLeads });
      if (response.success) {
        // Save source URLs as attachments for leads that have them
        if (response.saved && response.saved.length > 0) {
          for (const savedLead of response.saved) {
            const originalLead = selectedLeads.find(l => 
              l.ContactName === savedLead.lead.ContactName && 
              l.CompanyName === savedLead.lead.CompanyName
            );
            
            if (originalLead && originalLead.LinkedIn && originalLead.LinkedIn.trim()) {
              try {
                await api.post(`/attachments/Lead/${savedLead.leadId}/url`, {
                  url: originalLead.LinkedIn,
                  title: 'Source URL',
                  description: `Source URL for lead: ${originalLead.ContactName} at ${originalLead.CompanyName}`,
                  tags: 'source,url,lead-generation'
                });
                console.log(`✅ Source URL saved as attachment for lead ${savedLead.leadId}`);
              } catch (attachmentError) {
                console.warn(`⚠️ Failed to save source URL as attachment for lead ${savedLead.leadId}:`, attachmentError);
                // Don't fail the entire operation if attachment fails
              }
            }
          }
        }
        
        setGeneratedLeads(prev => prev.filter(lead => !selectedLeads.includes(lead)));
        setSelectedLeads([]);
        alert(`${selectedLeads.length} leads saved successfully!`);
      }
    } catch (error) {
      console.error('Error saving leads:', error);
      alert('Error saving leads. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const generateLeads = async () => {
    setIsGenerating(true);
    setError(null);
    setProgress(0);
    
    // DEBUG: Log search criteria before API call
    console.log('🔍 Frontend DEBUG - searchCriteria state:', searchCriteria);
    console.log('🔍 Frontend DEBUG - searchCriteria.criteria:', searchCriteria.criteria);
    console.log('🔍 Frontend DEBUG - searchCriteria.region:', searchCriteria.region);
    console.log('🔍 Frontend DEBUG - searchCriteria.language:', searchCriteria.language);
    console.log('🔍 Frontend DEBUG - maxLeads:', maxLeads);
    
    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 500);

      // Build sources array from user selection
      const sources = [];
      const prioritySources = [];
      
      // Add selected sources
      if (selectedSources['govspending-api']) {
        sources.push('govspending'); // Backend recognizes 'govspending' for API
        prioritySources.push('govspending');
      }
      if (selectedSources['dbd']) {
        sources.push('dbd');
        prioritySources.push('dbd');
      }
      if (selectedSources['tavily']) {
        sources.push('tavily');
      }
      if (selectedSources['linkedin']) {
        sources.push('linkedin');
      }
      if (selectedSources['websites']) {
        sources.push('websites');
      }
      if (selectedSources['b2b']) {
        sources.push('b2b_marketplaces');
      }
      if (selectedSources['public-directories']) {
        sources.push('public_directories');
      }
      
      // If no sources selected, use defaults
      if (sources.length === 0) {
        if (searchCriteria.region === 'th') {
          sources.push('govspending', 'tavily', 'linkedin', 'websites');
          prioritySources.push('govspending');
        } else {
          sources.push('linkedin', 'tavily', 'websites');
          prioritySources.push('linkedin');
        }
      }
      
      const apiPayload = {
        criteria: {
          keywords: searchCriteria.criteria, // Natural language criteria as keywords
          searchCriteria: searchCriteria.criteria, // Natural language criteria
          region: searchCriteria.region,
          language: searchCriteria.language,
          aiProcessing: true // Flag to indicate AI should process and screen results
        },
        sources: sources,
        prioritySources: prioritySources,
        maxLeads: maxLeads
      };
      
      console.log('🔍 Frontend DEBUG - API payload:', apiPayload);
      
      const response = await api.post('/ai/lead-generation', apiPayload);

      clearInterval(progressInterval);
      setProgress(100);
      
      if (response.leads && response.leads.length > 0) {
        // Process and validate leads before displaying
        const processedResult = processAndValidateLeads(response.leads);
        
        if (processedResult.leads.length > 0) {
          setGeneratedLeads(processedResult.leads);
          setProcessingStats(processedResult.stats);
          
          // Calculate source statistics
          const stats = calculateSourceStats(processedResult.leads);
          setSourceStats(stats);
          
          setCurrentStep(2);
          
          // Show processing stats if leads were filtered or split
          if (processedResult.stats.filtered > 0 || processedResult.stats.split > 0) {
            const statsMessage = [];
            if (processedResult.stats.split > 0) {
              statsMessage.push(`${processedResult.stats.split} combined contacts were separated`);
            }
            if (processedResult.stats.filtered > 0) {
              statsMessage.push(`${processedResult.stats.filtered} leads were filtered out due to insufficient contact information`);
            }
            console.log('📊 Processing stats:', statsMessage.join(', '));
          }
        } else {
          setError('No valid leads found. All results were filtered due to insufficient contact information. Please try adjusting your search criteria.');
        }
      } else {
        setError('No leads found matching your criteria. Try adjusting your search parameters.');
      }
    } catch (err) {
      setError(err.message || 'Failed to generate leads. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const toggleLeadSelection = (lead) => {
    setSelectedLeads(prev => {
      // Create a unique identifier for the lead
      const leadId = lead.id || `${lead.ContactName}-${lead.CompanyName}-${lead.ContactEmail}`;
      const isSelected = prev.some(l => {
        const existingId = l.id || `${l.ContactName}-${l.CompanyName}-${l.ContactEmail}`;
        return existingId === leadId;
      });
      
      if (isSelected) {
        return prev.filter(l => {
          const existingId = l.id || `${l.ContactName}-${l.CompanyName}-${l.ContactEmail}`;
          return existingId !== leadId;
        });
      } else {
        // Add the lead with a unique ID for tracking
        return [...prev, { ...lead, id: leadId }];
      }
    });
  };

  const selectAllLeads = () => {
    const allLeadsWithIds = generatedLeads.map(lead => ({
      ...lead,
      id: lead.id || `${lead.ContactName}-${lead.CompanyName}-${lead.ContactEmail}`
    }));
    setSelectedLeads(allLeadsWithIds);
  };

  const clearAllSelections = () => {
    setSelectedLeads([]);
  };

  const saveSelectedLeads = async () => {
    if (selectedLeads.length === 0) return;
    
    setIsGenerating(true);
    try {
      // Process and enhance all selected leads before saving
      const leadsToSave = selectedLeads.map(lead => {
        const processedLead = processLeadForSaving(lead);
        if (!processedLead.Title || !processedLead.Title.trim()) {
          processedLead.Title = generateLeadTitle(lead);
        }
        console.log('📝 Generated lead title:', processedLead.Title);
        return {
          ...processedLead,
          LeadSource: 'Auto Generated', // Standardized source for all LeadGen leads
          Status: 'New',
          Priority: 'Medium',
          Score: lead.aiScore || 75
        };
      });

      console.log('🔍 Processing', selectedLeads.length, 'leads for content enhancement...');

      await api.post('/leads/bulk', { leads: leadsToSave });
      
      // Navigate back to leads page
      navigate('/leads');
    } catch (err) {
      setError(err.message || 'Failed to save leads. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
    if (score >= 40) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-green-100 dark:bg-green-900';
    if (score >= 60) return 'bg-yellow-100 dark:bg-yellow-900';
    if (score >= 40) return 'bg-orange-100 dark:bg-orange-900';
    return 'bg-red-100 dark:bg-red-900';
  };

  // Validate that a lead has minimum required contact information
  const hasMinimumContactInfo = (lead) => {
    // Must have at least one of: CompanyName, ContactEmail, or ContactPhone
    // We prioritize leads that have CompanyName and (Email or Phone)
    const hasCompany = lead.CompanyName && lead.CompanyName.trim() && lead.CompanyName !== 'Unknown Company';
    const hasEmail = lead.ContactEmail && lead.ContactEmail.trim() && lead.ContactEmail.includes('@') && !lead.ContactEmail.includes('example');
    const hasPhone = lead.ContactPhone && lead.ContactPhone.trim();
    
    // Also allow if just Company Name is present (we can find contact later)
    // But ideally we want Company + (Email/Phone)
    return hasCompany || hasEmail || hasPhone;
  };

  // Detect if a field contains multiple contacts (separated by commas, semicolons, or newlines)
  const detectMultipleContacts = (text) => {
    if (!text || typeof text !== 'string') return [text];
    
    // Check for common separators
    const separators = [';', '\n', ','];
    for (const separator of separators) {
      if (text.includes(separator)) {
        const parts = text.split(separator)
          .map(part => part.trim())
          .filter(part => part.length > 0);
        
        if (parts.length > 1) {
          return parts;
        }
      }
    }
    
    return [text];
  };

  // Split leads that contain multiple contacts
  const splitCombinedContacts = (lead) => {
    const splits = [];
    
    // Check if ContactName contains multiple names
    const names = detectMultipleContacts(lead.ContactName);
    const emails = detectMultipleContacts(lead.ContactEmail);
    const phones = detectMultipleContacts(lead.ContactPhone);
    
    // If we have multiple entries in any field, split the lead
    const maxSplits = Math.max(names.length, emails.length, phones.length);
    
    if (maxSplits > 1) {
      console.log(`🔀 Splitting lead with ${maxSplits} contacts:`, lead.ContactName);
      
      for (let i = 0; i < maxSplits; i++) {
        const splitLead = {
          ...lead,
          ContactName: names[i] || names[0] || lead.ContactName,
          ContactEmail: emails[i] || emails[0] || lead.ContactEmail,
          ContactPhone: phones[i] || phones[0] || lead.ContactPhone,
          _splitFrom: lead.ContactName,
          _splitIndex: i + 1,
          _splitTotal: maxSplits
        };
        splits.push(splitLead);
      }
      
      return splits;
    }
    
    return [lead];
  };

  // Process and validate leads from API response
  const processAndValidateLeads = (leads) => {
    let processed = [];
    let filtered = 0;
    let split = 0;
    
    for (const lead of leads) {
      // First, try to split combined contacts
      const splitLeads = splitCombinedContacts(lead);
      
      if (splitLeads.length > 1) {
        split += splitLeads.length - 1;
      }
      
      // Then validate each split lead has minimum contact info
      for (const splitLead of splitLeads) {
        if (hasMinimumContactInfo(splitLead)) {
          processed.push(splitLead);
        } else {
          filtered++;
          console.warn('⚠️ Filtered out lead due to insufficient contact information:', splitLead);
        }
      }
    }
    
    // Sort leads to prioritize those with Company Name AND (Email OR Phone)
    processed.sort((a, b) => {
      const aHasCompany = a.CompanyName && a.CompanyName !== 'Unknown Company';
      const bHasCompany = b.CompanyName && b.CompanyName !== 'Unknown Company';
      
      const aHasEmail = a.ContactEmail && a.ContactEmail.includes('@') && !a.ContactEmail.includes('example');
      const bHasEmail = b.ContactEmail && b.ContactEmail.includes('@') && !b.ContactEmail.includes('example');
      
      const aHasPhone = a.ContactPhone && a.ContactPhone.length > 5;
      const bHasPhone = b.ContactPhone && b.ContactPhone.length > 5;
      
      // Score calculation for sorting (higher is better)
      // 1. Company + Email (Most preferred) - Score 100
      // 2. Company + Phone - Score 80
      // 3. Company only - Score 50
      // 4. Email only - Score 40
      // 5. Phone only - Score 30
      // 6. Others - Score 0
      
      let aScore = 0;
      if (aHasCompany) {
        if (aHasEmail) aScore = 100;
        else if (aHasPhone) aScore = 80;
        else aScore = 50;
      } else {
        if (aHasEmail) aScore = 40;
        else if (aHasPhone) aScore = 30;
      }
      
      let bScore = 0;
      if (bHasCompany) {
        if (bHasEmail) bScore = 100;
        else if (bHasPhone) bScore = 80;
        else bScore = 50;
      } else {
        if (bHasEmail) bScore = 40;
        else if (bHasPhone) bScore = 30;
      }
      
      if (aScore !== bScore) return bScore - aScore;
      
      // Secondary sort by AI Score
      return (b.aiScore || 0) - (a.aiScore || 0);
    });

    console.log(`✅ Processed ${leads.length} leads → ${processed.length} valid leads (${filtered} filtered, ${split} split)`);
    
    return {
      leads: processed,
      stats: {
        original: leads.length,
        valid: processed.length,
        filtered,
        split
      }
    };
  };

  // Calculate source statistics from leads
  const calculateSourceStats = (leads) => {
    const stats = {};
    leads.forEach(lead => {
      const source = lead.source || 'AI Generated';
      // Normalize source names - check for API first (more specific)
      let normalizedSource = 'other';
      
      // Check for GovSpending API (official API, not Tavily)
      if (source.includes('GovSpending API') || 
          source.includes('จัดซื้อจัดจ้างภาครัฐ (GovSpending API)') ||
          source.includes('GovSpending API - Company')) {
        normalizedSource = 'govspending-api';
      } 
      // Check for GovSpending from Tavily search (fallback method)
      else if ((source.includes('GovSpending') || source.includes('จัดซื้อจัดจ้างภาครัฐ')) 
                 && !source.includes('API')) {
        normalizedSource = 'govspending-tavily';
      } 
      // Check for other Tavily sources
      else if (source.includes('via Tavily') || source.includes('Tavily')) {
        normalizedSource = 'tavily';
      } 
      else if (source.includes('DBD') || source.includes('กรมพัฒนา')) {
        normalizedSource = 'dbd';
      } else if (source.includes('LinkedIn')) {
        normalizedSource = 'linkedin';
      } else if (source.includes('.co.th') || source.includes('Thai Business')) {
        normalizedSource = 'thai-websites';
      } else if (source.includes('B2B')) {
        normalizedSource = 'b2b';
      } else {
        normalizedSource = 'other';
      }
      
      stats[normalizedSource] = (stats[normalizedSource] || 0) + 1;
      stats[normalizedSource + '_display'] = source; // Keep original for display
    });
    return stats;
  };

  // Filter leads by source
  const getFilteredLeads = () => {
    if (selectedSource === 'all') {
      return generatedLeads;
    }
    
    return generatedLeads.filter(lead => {
      const source = lead.source || 'AI Generated';
      if (selectedSource === 'govspending-api') {
        return source.includes('GovSpending API') || 
               source.includes('จัดซื้อจัดจ้างภาครัฐ (GovSpending API)') ||
               source.includes('GovSpending API - Company');
      } else if (selectedSource === 'govspending-tavily') {
        return (source.includes('GovSpending') || source.includes('จัดซื้อจัดจ้างภาครัฐ')) 
               && !source.includes('API');
      } else if (selectedSource === 'tavily') {
        return source.includes('via Tavily') || source.includes('Tavily');
      } else if (selectedSource === 'dbd') {
        return source.includes('DBD') || source.includes('กรมพัฒนา');
      } else if (selectedSource === 'linkedin') {
        return source.includes('LinkedIn');
      } else if (selectedSource === 'thai-websites') {
        return source.includes('.co.th') || source.includes('Thai Business');
      } else if (selectedSource === 'b2b') {
        return source.includes('B2B');
      }
      return true;
    });
  };

  // Get source badge info
  const getSourceBadge = (source) => {
    if (!source) return { label: 'AI Generated', color: 'gray', icon: '✨' };
    
    // Check for GovSpending (official government data, not Tavily)
    if (source.includes('GovSpending API') || 
        source.includes('จัดซื้อจัดจ้างภาครัฐ (GovSpending API)') ||
        source.includes('GovSpending API - Company') ||
        source.includes('Thailand GovSpending')) {
      return { label: 'ระบบจัดซื้อจัดจ้างฯ', color: 'blue', icon: '🏛️', tooltip: 'ระบบจัดซื้อจัดจ้างภาครัฐ - Government Procurement' };
    } 
    // Check for GovSpending from Tavily search
    else if ((source.includes('GovSpending') || source.includes('จัดซื้อจัดจ้างภาครัฐ')) 
             && !source.includes('API') && !source.includes('Thailand')) {
      return { label: 'จัดซื้อจัดจ้าง (ค้นหา)', color: 'indigo', icon: '🔍', tooltip: 'GovSpending via Web Search' };
    } 
    // Check for other Tavily sources
    else if (source.includes('via Tavily') || source.includes('Tavily')) {
      return { label: 'ค้นหา AI', color: 'cyan', icon: '🔍', tooltip: 'AI-powered web search' };
    } 
    else if (source.includes('DBD') || source.includes('กรมพัฒนา')) {
      return { label: 'กรมพัฒนาฯ', color: 'green', icon: '🏢', tooltip: 'กรมพัฒนาธุรกิจการค้า - DBD' };
    } else if (source.includes('LinkedIn')) {
      return { label: 'LinkedIn', color: 'blue', icon: '💼', tooltip: 'LinkedIn Profile' };
    } else if (source.includes('.co.th') || source.includes('Thai Business')) {
      return { label: 'เว็บไซต์ไทย', color: 'purple', icon: '🌐', tooltip: 'Thai Business Website' };
    } else if (source.includes('B2B')) {
      return { label: 'ตลาดกลาง B2B', color: 'orange', icon: '🛒', tooltip: 'B2B Marketplace' };
    }
    return { label: source, color: 'gray', icon: '✨', tooltip: source };
  };

  const getScoreReasons = (lead) => {
    const reasons = [];
    const score = lead.aiScore || lead.Score || 75;
    
    // Reason based on data completeness
    let completenessScore = 0;
    if (lead.ContactEmail) completenessScore += 25;
    if (lead.ContactPhone) completenessScore += 15;
    if (lead.LinkedIn || lead.Website) completenessScore += 20;
    if (lead.JobTitle) completenessScore += 15;
    if (lead.Description) completenessScore += 10;
    
    if (completenessScore >= 60) {
      reasons.push(`✅ ข้อมูลครบถ้วน (+${completenessScore}%)`);
    } else if (completenessScore >= 40) {
      reasons.push(`⚠️ ข้อมูลพอใช้ (+${completenessScore}%)`);
    } else {
      reasons.push(`❌ ข้อมูลไม่ครบ (+${completenessScore}%)`);
    }
    
    // Reason based on source
    if (lead.source) {
      if (lead.source.includes('GovSpending API') || lead.source.includes('จัดซื้อจัดจ้างภาครัฐ (GovSpending API)')) {
        reasons.push('✅ ระบบจัดซื้อจัดจ้างภาครัฐ API (Official) (+25%)');
      } else if ((lead.source.includes('GovSpending') || lead.source.includes('จัดซื้อจัดจ้างภาครัฐ')) 
                 && !lead.source.includes('API')) {
        reasons.push('✅ ระบบจัดซื้อจัดจ้างภาครัฐ (via Tavily Search) (+20%)');
      } else if (lead.source.includes('via Tavily') || lead.source.includes('Tavily')) {
        reasons.push('✅ Tavily AI Search (+15%)');
      } else if (lead.source.includes('DBD') || lead.source.includes('กรมพัฒนา')) {
        reasons.push('✅ แหล่งข้อมูลทางการ (+15%)');
      } else if (lead.source.includes('LinkedIn')) {
        reasons.push('✅ โปรไฟล์ LinkedIn (+10%)');
      } else if (lead.source.includes('.co.th') || lead.source.includes('Thai Business')) {
        reasons.push('✅ เว็บไซต์บริษัทไทย (+10%)');
      }
    }
    
    // Reason based on contact info quality
    if (lead.ContactEmail && lead.ContactEmail.includes('@') && !lead.ContactEmail.includes('example')) {
      reasons.push('✅ อีเมลที่ตรวจสอบได้ (+10%)');
    }
    
    if (lead.ContactPhone) {
      reasons.push('✅ มีเบอร์โทรศัพท์ (+10%)');
    }
    
    // Reason based on company info
    if (lead.CompanyName && lead.CompanyName !== 'Unknown Company') {
      reasons.push('✅ ชื่อบริษัทชัดเจน (+5%)');
    }
    
    // Reason based on job title relevance
    if (lead.JobTitle && (
      lead.JobTitle.includes('Director') || 
      lead.JobTitle.includes('Manager') || 
      lead.JobTitle.includes('CEO') || 
      lead.JobTitle.includes('CTO') ||
      lead.JobTitle.includes('ผู้จัดการ') ||
      lead.JobTitle.includes('กรรมการ')
    )) {
      reasons.push('✅ ตำแหน่งระดับผู้บริหาร (+10%)');
    }
    
    // Add qualification level
    const qualLevel = lead.QualificationLevel || 'Warm';
    if (qualLevel === 'Hot') {
      reasons.push('🔥 คุณภาพสูง (Hot Lead)');
    } else if (qualLevel === 'Warm') {
      reasons.push('🌟 คุณภาพดี (Warm Lead)');
    }
    
    return {
      score,
      reasons,
      summary: score >= 80 ? 'Lead คุณภาพสูง' : score >= 60 ? 'Lead คุณภาพดี' : score >= 40 ? 'Lead คุณภาพปานกลาง' : 'Lead ต้องติดตามเพิ่มเติม'
    };
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/leads')}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-4 w-4 dark:text-gray-300" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
              <Sparkles className="h-6 w-6 text-purple-600" />
              <span>AI Lead Generation / สร้างลีดด้วย AI</span>
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              ค้นหาธุรกิจไทยและทั่วโลก / Find Thai businesses and worldwide
            </p>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center space-x-4">
          <div className={`flex items-center space-x-2 ${currentStep >= 1 ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>
              {currentStep > 1 ? <CheckCircle className="h-4 w-4" /> : '1'}
            </div>
            <span className="font-medium">Define Criteria</span>
          </div>
          <div className="flex-1 h-0.5 bg-gray-200 dark:bg-gray-700"></div>
          <div className={`flex items-center space-x-2 ${currentStep >= 2 ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>
              {currentStep > 2 ? <CheckCircle className="h-4 w-4" /> : '2'}
            </div>
            <span className="font-medium">Review Leads</span>
          </div>
          <div className="flex-1 h-0.5 bg-gray-200 dark:bg-gray-700"></div>
          <div className={`flex items-center space-x-2 ${currentStep >= 3 ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-gray-700'}`}>
              3
            </div>
            <span className="font-medium">Save Leads</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        </div>
      )}

      {/* Step 1: Define Search Criteria */}
      {currentStep === 1 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center space-x-2 mb-6">
            <Target className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            <h3 className="text-lg font-medium dark:text-white">
              {searchCriteria.region === 'th' ? 'ค้นหาธุรกิจไทย 🇹🇭' : 'LinkedIn Lead Generation 🌎'}
            </h3>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* LEFT COLUMN: Search Criteria & Settings */}
            <div className="space-y-6">
              {/* Region Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  ภูมิภาคการค้นหา / Search Region *
                </label>
                <select
                  value={searchCriteria.region}
                  onChange={(e) => {
                    handleCriteriaChange('region', e.target.value);
                    handleCriteriaChange('language', e.target.value === 'th' ? 'th' : 'en');
                  }}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-lg font-medium"
                >
                  <option value="th">🇹🇭 THAILAND (ธุรกิจไทย / Thai Businesses)</option>
                  <option value="global">🌎 GLOBAL (Worldwide International)</option>
                </select>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {searchCriteria.region === 'th' 
                    ? 'ค้นหาธุรกิจไทยจากแหล่งข้อมูลราชการและเอกชน'
                    : 'Search international professionals from various sources'}
                </p>
              </div>

              {/* Search Criteria Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  เกณฑ์การค้นหา / Search Criteria *
                </label>
                <textarea
                  value={searchCriteria.criteria}
                  onChange={(e) => handleCriteriaChange('criteria', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-600 text-lg min-h-[180px] resize-vertical"
                  placeholder={searchCriteria.region === 'th' 
                    ? "เช่น ต้องการหาธุรกิจเทคโนโลยีที่ทำซอฟต์แวร์ในกรุงเทพฯ มีผู้จัดการฝ่ายขายที่พูดภาษาอังกฤษได้ และมีประสบการณ์ในอุตสาหกรรม B2B" 
                    : "e.g., Looking for technology companies in Bangkok that develop software, have sales managers who speak English, and have experience in B2B industry"}
                />
                <div className="mt-2 space-y-1">
                  {searchCriteria.region === 'th' ? (
                    <>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        อธิบายความต้องการของคุณเป็นประโยค AI จะวิเคราะห์และสร้างเกณฑ์การค้นหาที่เหมาะสม
                      </p>
                      <p className="text-sm font-medium text-blue-600 dark:text-blue-400">
                        💡 ตัวอย่าง: "ผู้รับเหมาก่อสร้างที่ได้งานภาครัฐ", "บริษัทไอทีที่ให้บริการระบบราชการ", "ธุรกิจอาหารที่ส่งออก"
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Describe your requirements in natural language. AI will analyze and create appropriate search criteria.
                      </p>
                      <p className="text-sm font-medium text-blue-600 dark:text-blue-400">
                        💡 Examples: "Technology companies in Southeast Asia", "Sales directors with B2B experience"
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* Number of Leads */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {searchCriteria.region === 'th' ? 'จำนวนธุรกิจที่ต้องการ' : 'Number of Leads to Generate'}
                </label>
                <select
                  value={maxLeads}
                  onChange={(e) => setMaxLeads(parseInt(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-lg"
                >
                  <option value={5}>{searchCriteria.region === 'th' ? '5 ธุรกิจ' : '5 leads'}</option>
                  <option value={10}>{searchCriteria.region === 'th' ? '10 ธุรกิจ' : '10 leads'}</option>
                  <option value={15}>{searchCriteria.region === 'th' ? '15 ธุรกิจ' : '15 leads'}</option>
                  <option value={20}>{searchCriteria.region === 'th' ? '20 ธุรกิจ' : '20 leads'}</option>
                  <option value={25}>{searchCriteria.region === 'th' ? '25 ธุรกิจ' : '25 leads'}</option>
                  <option value={30}>{searchCriteria.region === 'th' ? '30 ธุรกิจ' : '30 leads'}</option>
                  <option value={50}>{searchCriteria.region === 'th' ? '50 ธุรกิจ' : '50 leads'}</option>
                </select>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {searchCriteria.region === 'th' 
                    ? 'เลือกจำนวนธุรกิจไทยที่ต้องการค้นหาและวิเคราะห์'
                    : 'Choose how many leads to find and analyze.'}
                </p>
              </div>

              {/* Generate Button */}
              <div className="flex justify-center mt-6">
                <button
                  onClick={generateLeads}
                  disabled={isGenerating || !searchCriteria.criteria.trim()}
                  className="px-8 py-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-3 text-lg font-medium"
                >
                  {isGenerating ? (
                    <>
                      <Loader className="h-5 w-5 animate-spin" />
                      <span>
                          {searchCriteria.region === 'th' 
                            ? `AI กำลังวิเคราะห์และค้นหาธุรกิจไทย ${maxLeads} แห่ง...`
                            : `AI is analyzing and searching for ${maxLeads} Leads...`}
                      </span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-5 w-5" />
                      <span>
                          {searchCriteria.region === 'th'
                            ? `AI ค้นหา ${maxLeads} ธุรกิจไทย`
                            : `AI Find ${maxLeads} Leads`}
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* RIGHT COLUMN: Data Source Selection */}
            <div>
              <div className="sticky top-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {searchCriteria.region === 'th' ? '🎯 เลือกแหล่งข้อมูล / Select Data Sources *' : '🎯 Select Data Sources *'}
                </label>
                
                {/* Select All / Deselect All Buttons */}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {Object.values(selectedSources).filter(v => v).length} {searchCriteria.region === 'th' ? 'แหล่งที่เลือก' : 'sources selected'}
                  </span>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => setSelectedSources({
                        'govspending-api': true,
                        'dbd': true,
                        'tavily': true,
                        'linkedin': true,
                        'websites': true,
                        'b2b': true,
                        'public-directories': true
                      })}
                      className="px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline"
                    >
                      {searchCriteria.region === 'th' ? 'เลือกทั้งหมด' : 'Select All'}
                    </button>
                    <span className="text-gray-300 dark:text-gray-600">|</span>
                    <button
                      type="button"
                      onClick={() => setSelectedSources({
                        'govspending-api': false,
                        'dbd': false,
                        'tavily': false,
                        'linkedin': false,
                        'websites': false,
                        'b2b': false,
                        'public-directories': false
                      })}
                      className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:underline"
                    >
                      {searchCriteria.region === 'th' ? 'ยกเลิกทั้งหมด' : 'Deselect All'}
                    </button>
                  </div>
                </div>

                {/* Official Government Data */}
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-2">
                    🏛️ {searchCriteria.region === 'th' ? 'ข้อมูลราชการ (Government Data)' : 'Official Government Data'}
                  </h4>
                  <div className="space-y-2">
                    {searchCriteria.region === 'th' && (
                      <label className="flex items-start space-x-3 p-3 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          checked={selectedSources['govspending-api']}
                          onChange={(e) => setSelectedSources(prev => ({
                            ...prev,
                            'govspending-api': e.target.checked
                          }))}
                          className="mt-1 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-gray-900 dark:text-white text-sm">🏛️ ระบบจัดซื้อจัดจ้างภาครัฐ (ภาษีไปไหน?)</span>
                            <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">แนะนำ</span>
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 font-medium">
                            GovSpending - Government Procurement
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            ข้อมูลสัญญาและบริษัทที่ชนะการประกวดราคาจากภาครัฐ (govspending.data.go.th)
                          </p>
                        </div>
                      </label>
                    )}
                    <label className="flex items-start space-x-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors opacity-50">
                      <input
                        type="checkbox"
                        checked={selectedSources['dbd']}
                        onChange={(e) => setSelectedSources(prev => ({
                          ...prev,
                          'dbd': e.target.checked
                        }))}
                        disabled
                        className="mt-1 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900 dark:text-white text-sm">🏢 กรมพัฒนาธุรกิจการค้า</span>
                          <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">เร็วๆ นี้</span>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 font-medium">
                          DBD - Department of Business Development
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          ข้อมูลทะเบียนธุรกิจจากกรมพัฒนาธุรกิจการค้า กระทรวงพาณิชย์
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Web Search Sources */}
                <div>
                  <h4 className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide mb-2">
                    🔍 {searchCriteria.region === 'th' ? 'การค้นหาเว็บและโซเชียล (Web & Social Search)' : 'Web & Social Search'}
                  </h4>
                  <div className="space-y-2">
                    <label className="flex items-start space-x-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedSources['tavily']}
                        onChange={(e) => setSelectedSources(prev => ({
                          ...prev,
                          'tavily': e.target.checked
                        }))}
                        className="mt-1 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <span className="font-medium text-gray-900 dark:text-white text-sm">🔍 การค้นหาด้วย AI</span>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 font-medium">
                          Tavily AI Search
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          {searchCriteria.region === 'th' ? 'ค้นหาข้อมูลจากเว็บไซต์ทั่วไปด้วย AI' : 'AI-powered web search'}
                        </p>
                      </div>
                    </label>
                    <label className="flex items-start space-x-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedSources['linkedin']}
                        onChange={(e) => setSelectedSources(prev => ({
                          ...prev,
                          'linkedin': e.target.checked
                        }))}
                        className="mt-1 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <span className="font-medium text-gray-900 dark:text-white text-sm">💼 ลิงค์อิน</span>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 font-medium">
                          LinkedIn
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          {searchCriteria.region === 'th' ? 'โปรไฟล์มืออาชีพ' : 'Professional profiles'}
                        </p>
                      </div>
                    </label>
                    {searchCriteria.region === 'th' && (
                      <>
                        <label className="flex items-start space-x-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors">
                          <input
                            type="checkbox"
                            checked={selectedSources['websites']}
                            onChange={(e) => setSelectedSources(prev => ({
                              ...prev,
                              'websites': e.target.checked
                            }))}
                            className="mt-1 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <span className="font-medium text-gray-900 dark:text-white text-sm">🌐 เว็บไซต์ธุรกิจไทย</span>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 font-medium">
                              Thai Business Websites (.co.th)
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                              เว็บไซต์บริษัทไทยโดเมน .co.th
                            </p>
                          </div>
                        </label>
                        <label className="flex items-start space-x-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors">
                          <input
                            type="checkbox"
                            checked={selectedSources['b2b']}
                            onChange={(e) => setSelectedSources(prev => ({
                              ...prev,
                              'b2b': e.target.checked
                            }))}
                            className="mt-1 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <span className="font-medium text-gray-900 dark:text-white text-sm">🛒 ตลาดกลางธุรกิจ</span>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 font-medium">
                              B2B Marketplaces
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                              แหล่งขายส่งและตลาดกลางธุรกิจออนไลน์
                            </p>
                          </div>
                        </label>
                      </>
                    )}
                    <label className="flex items-start space-x-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={selectedSources['public-directories']}
                        onChange={(e) => setSelectedSources(prev => ({
                          ...prev,
                          'public-directories': e.target.checked
                        }))}
                        className="mt-1 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <span className="font-medium text-gray-900 dark:text-white text-sm">📖 ไดเรกทอรีธุรกิจ</span>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 font-medium">
                          Business Directories
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          {searchCriteria.region === 'th' ? 'ทะเบียนธุรกิจสาธารณะ' : 'Public business listings'}
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Review Generated Leads */}
      {currentStep === 2 && (
        <div className="space-y-6">
          {/* Search Criteria Summary */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                {searchCriteria.region === 'th' ? (
                  <span className="text-2xl">🔍</span>
                ) : (
                  <Search className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                )}
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">
                  {searchCriteria.region === 'th' ? 'เกณฑ์การค้นหา / Search Criteria' : 'Search Criteria'}
                </h4>
                <div className="space-y-2">
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-blue-100 dark:border-blue-900">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {searchCriteria.region === 'th' ? 'ความต้องการ:' : 'Criteria:'}
                    </p>
                    <p className="text-sm text-gray-900 dark:text-white italic">
                      "{searchCriteria.criteria}"
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                      {searchCriteria.region === 'th' ? '🇹🇭 ภูมิภาค: ประเทศไทย' : '🌎 Region: Global'}
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                      {searchCriteria.region === 'th' ? `📊 จำนวน: ${maxLeads} ธุรกิจ` : `📊 Requested: ${maxLeads} leads`}
                    </span>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                      ✅ {searchCriteria.region === 'th' ? `พบ: ${generatedLeads.length} ธุรกิจ` : `Found: ${generatedLeads.length} leads`}
                    </span>
                    {sourceStats['govspending-api'] && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                        🏛️ ระบบจัดซื้อจัดจ้างฯ: {sourceStats['govspending-api']}
                      </span>
                    )}
                    {sourceStats['govspending-tavily'] && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200">
                        🔍 จัดซื้อจัดจ้าง (ค้นหา): {sourceStats['govspending-tavily']}
                      </span>
                    )}
                    {sourceStats['tavily'] && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-cyan-100 dark:bg-cyan-900 text-cyan-800 dark:text-cyan-200">
                        🔍 ค้นหา AI: {sourceStats['tavily']}
                      </span>
                    )}
                    {sourceStats['dbd'] && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                        🏢 กรมพัฒนาฯ: {sourceStats['dbd']}
                      </span>
                    )}
                    {processingStats && processingStats.split > 0 && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                        🔀 {searchCriteria.region === 'th' ? `แยกผู้ติดต่อ: ${processingStats.split}` : `Split: ${processingStats.split}`}
                      </span>
                    )}
                    {processingStats && processingStats.filtered > 0 && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
                        ⚠️ {searchCriteria.region === 'th' ? `กรอง: ${processingStats.filtered}` : `Filtered: ${processingStats.filtered}`}
                      </span>
                    )}
                  </div>
                  {processingStats && (processingStats.split > 0 || processingStats.filtered > 0) && (
                    <div className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-800">
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        {searchCriteria.region === 'th' ? (
                          <>
                            ✨ ผลลัพธ์ถูกประมวลผลโดยอัตโนมัติ: 
                            {processingStats.split > 0 && ` แยกผู้ติดต่อที่รวมกัน ${processingStats.split} รายการ`}
                            {processingStats.split > 0 && processingStats.filtered > 0 && ` และ`}
                            {processingStats.filtered > 0 && ` กรองข้อมูลที่ไม่มีข้อมูลติดต่อ ${processingStats.filtered} รายการ`}
                          </>
                        ) : (
                          <>
                            ✨ Results were automatically processed: 
                            {processingStats.split > 0 && ` ${processingStats.split} combined contacts separated`}
                            {processingStats.split > 0 && processingStats.filtered > 0 && ` and`}
                            {processingStats.filtered > 0 && ` ${processingStats.filtered} leads filtered due to missing contact info`}
                          </>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => setCurrentStep(1)}
                className="flex-shrink-0 px-3 py-1.5 text-xs bg-white dark:bg-gray-800 border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
              >
                {searchCriteria.region === 'th' ? 'แก้ไขเกณฑ์' : 'Edit Criteria'}
              </button>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Users className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <div>
                <h3 className="text-lg font-medium dark:text-white">
                  Generated Leads ({generatedLeads.length})
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Click "View Details" to see full lead information and save individually, or use bulk actions below
                </p>
              </div>
            </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedLeads.length} of {generatedLeads.length} selected
                </span>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={selectAllLeads}
                    className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    Select All
                  </button>
                  <button
                    onClick={clearAllSelections}
                    className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    Clear All
                  </button>
                </div>
                <button
                  onClick={() => setCurrentStep(3)}
                  disabled={selectedLeads.length === 0}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  <Save className="h-4 w-4" />
                  <span>Save Selected ({selectedLeads.length})</span>
                </button>
              </div>
            </div>

            {/* Source Filter and Statistics */}
            {Object.keys(sourceStats).length > 0 && (
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Filter by Source / กรองตามแหล่งข้อมูล:
                  </h4>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Showing {getFilteredLeads().length} of {generatedLeads.length} leads
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedSource('all')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      selectedSource === 'all'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500'
                    }`}
                  >
                    All ({generatedLeads.length})
                  </button>
                  {sourceStats['govspending-api'] && (
                    <button
                      onClick={() => setSelectedSource('govspending-api')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center space-x-1 ${
                        selectedSource === 'govspending-api'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500'
                      }`}
                    >
                      <span>🏛️</span>
                      <span>ระบบจัดซื้อจัดจ้างภาครัฐ (ภาษีไปไหน?) ({sourceStats['govspending-api']})</span>
                    </button>
                  )}
                  {sourceStats['govspending-tavily'] && (
                    <button
                      onClick={() => setSelectedSource('govspending-tavily')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center space-x-1 ${
                        selectedSource === 'govspending-tavily'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500'
                      }`}
                    >
                      <span>🔍</span>
                      <span>จัดซื้อจัดจ้าง (ค้นหา) ({sourceStats['govspending-tavily']})</span>
                    </button>
                  )}
                  {sourceStats['tavily'] && (
                    <button
                      onClick={() => setSelectedSource('tavily')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center space-x-1 ${
                        selectedSource === 'tavily'
                          ? 'bg-cyan-600 text-white'
                          : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500'
                      }`}
                    >
                      <span>🔍</span>
                      <span>ค้นหา AI ({sourceStats['tavily']})</span>
                    </button>
                  )}
                  {sourceStats['dbd'] && (
                    <button
                      onClick={() => setSelectedSource('dbd')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center space-x-1 ${
                        selectedSource === 'dbd'
                          ? 'bg-green-600 text-white'
                          : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500'
                      }`}
                    >
                      <span>🏢</span>
                      <span>กรมพัฒนาฯ ({sourceStats['dbd']})</span>
                    </button>
                  )}
                  {sourceStats['linkedin'] && (
                    <button
                      onClick={() => setSelectedSource('linkedin')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center space-x-1 ${
                        selectedSource === 'linkedin'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500'
                      }`}
                    >
                      <span>💼</span>
                      <span>ลิงค์อิน ({sourceStats['linkedin']})</span>
                    </button>
                  )}
                  {sourceStats['thai-websites'] && (
                    <button
                      onClick={() => setSelectedSource('thai-websites')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center space-x-1 ${
                        selectedSource === 'thai-websites'
                          ? 'bg-purple-600 text-white'
                          : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500'
                      }`}
                    >
                      <span>🌐</span>
                      <span>เว็บไซต์ไทย ({sourceStats['thai-websites']})</span>
                    </button>
                  )}
                  {sourceStats['b2b'] && (
                    <button
                      onClick={() => setSelectedSource('b2b')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center space-x-1 ${
                        selectedSource === 'b2b'
                          ? 'bg-orange-600 text-white'
                          : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500'
                      }`}
                    >
                      <span>🛒</span>
                      <span>ตลาดกลาง B2B ({sourceStats['b2b']})</span>
                    </button>
                  )}
                  {sourceStats['other'] && (
                    <button
                      onClick={() => setSelectedSource('other')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center space-x-1 ${
                        selectedSource === 'other'
                          ? 'bg-gray-600 text-white'
                          : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500'
                      }`}
                    >
                      <span>✨</span>
                      <span>Other ({sourceStats['other']})</span>
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {getFilteredLeads().map((lead, index) => {
                const leadId = lead.id || `${lead.ContactName}-${lead.CompanyName}-${lead.ContactEmail}`;
                const isSelected = selectedLeads.some(l => {
                  const existingId = l.id || `${l.ContactName}-${l.CompanyName}-${l.ContactEmail}`;
                  return existingId === leadId;
                });
                return (
                  <div
                    key={lead.id || index}
                    className="border rounded-lg p-4 transition-all duration-200 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                  >
                    {/* Show split indicator if this lead was split from combined contacts */}
                    {lead._splitFrom && (
                      <div className="mb-2 flex items-center space-x-1 text-xs text-blue-600 dark:text-blue-400">
                        <Users className="h-3 w-3" />
                        <span>Split contact {lead._splitIndex} of {lead._splitTotal}</span>
                      </div>
                    )}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-1">
                          {lead.ContactName || 'Unknown Contact'}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {lead.JobTitle || ''}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {lead.CompanyName || lead.ContactName || ''}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLeadSelect(lead);
                          }}
                          className="px-3 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-full transition-colors"
                        >
                          View Details
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSaveLead(lead);
                          }}
                          disabled={isSaving}
                          className="px-3 py-1 text-xs bg-green-100 hover:bg-green-200 text-green-700 rounded-full transition-colors disabled:opacity-50"
                        >
                          {isSaving ? 'Saving...' : 'Save'}
                        </button>
                        <div 
                          className={`relative px-2 py-1 rounded-full text-xs font-medium cursor-help ${getScoreBgColor(lead.aiScore || 75)} ${getScoreColor(lead.aiScore || 75)}`}
                          onMouseEnter={() => setHoveredLeadScore(leadId)}
                          onMouseLeave={() => setHoveredLeadScore(null)}
                        >
                          {lead.aiScore || 75}% Match
                          
                          {/* Tooltip */}
                          {hoveredLeadScore === leadId && (
                            <div className="absolute z-50 w-72 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl top-full right-0 mt-2"
                                 onClick={(e) => e.stopPropagation()}>
                              <div className="space-y-2">
                                <div className="flex items-center justify-between pb-2 border-b border-gray-200 dark:border-gray-700">
                                  <h5 className="font-semibold text-sm text-gray-900 dark:text-white">
                                    เหตุผลการให้คะแนน
                                  </h5>
                                  <span className={`text-lg font-bold ${getScoreColor(lead.aiScore || 75)}`}>
                                    {lead.aiScore || 75}%
                                  </span>
                                </div>
                                
                                <div className="text-xs">
                                  <p className="font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {getScoreReasons(lead).summary}
                                  </p>
                                  
                                  <div className="space-y-1">
                                    {getScoreReasons(lead).reasons.map((reason, idx) => (
                                      <div key={idx} className="flex items-start space-x-1">
                                        <span className="text-gray-600 dark:text-gray-400 leading-tight">
                                          {reason}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                  
                                  <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                                    <p className="text-xs text-gray-500 dark:text-gray-500 italic">
                                      💡 คะแนนคำนวณจากความครบถ้วนและคุณภาพของข้อมูล
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center">
                          {isSelected ? (
                            <CheckSquare className="h-5 w-5 text-blue-600" />
                          ) : (
                            <Square className="h-5 w-5 text-gray-400 hover:text-blue-600" />
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                      {lead.LinkedIn && (
                        <div className="flex items-center space-x-2">
                          <Globe className="h-3 w-3 text-blue-600" />
                          <a 
                            href={lead.LinkedIn} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium flex items-center space-x-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span>Source URL</span>
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      )}
                      {lead.ContactEmail && (
                        <div className="flex items-center space-x-2">
                          <Mail className="h-3 w-3" />
                          <span>{lead.ContactEmail}</span>
                        </div>
                      )}
                      {lead.ContactPhone && (
                        <div className="flex items-center space-x-2">
                          <Phone className="h-3 w-3" />
                          <span>{lead.ContactPhone}</span>
                        </div>
                      )}
                      {lead.Website && (
                        <div className="flex items-center space-x-2">
                          <Globe className="h-3 w-3" />
                          <a 
                            href={lead.Website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {lead.Website}
                          </a>
                        </div>
                      )}
                      {lead.Industry && (
                        <div className="flex items-center space-x-2">
                          <Building className="h-3 w-3" />
                          <span>{lead.Industry}</span>
                        </div>
                      )}
                      {lead.Location && (
                        <div className="flex items-center space-x-2">
                          <MapPin className="h-3 w-3" />
                          <span>{lead.Location}</span>
                        </div>
                      )}
                    </div>

                    {lead.Description && (
                      <div className="mt-3">
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3 whitespace-pre-line">
                          {lead.Description}
                        </p>
                        <div className="mt-1 flex items-center text-xs text-gray-500 dark:text-gray-500">
                          <Sparkles className="h-3 w-3 mr-1" />
                          <span>Content will be enhanced when saved</span>
                        </div>
                      </div>
                    )}

                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {(() => {
                          const badge = getSourceBadge(lead.source);
                          return (
                            <span 
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium cursor-help ${
                                badge.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' :
                                badge.color === 'indigo' ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200' :
                                badge.color === 'cyan' ? 'bg-cyan-100 dark:bg-cyan-900 text-cyan-800 dark:text-cyan-200' :
                                badge.color === 'green' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                                badge.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200' :
                                badge.color === 'orange' ? 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200' :
                                'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                              }`}
                              title={badge.tooltip || badge.label}
                            >
                              <span className="mr-1">{badge.icon}</span>
                              <span>{badge.label}</span>
                            </span>
                          );
                        })()}
                        {lead.socialMedia && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            • {lead.socialMedia}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-1">
                        <Star className="h-3 w-3 text-yellow-500" />
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {lead.confidence || 'High'} Confidence
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Save Leads */}
      {currentStep === 3 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center space-x-2 mb-6">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <h3 className="text-lg font-medium dark:text-white">Save Selected Leads</h3>
          </div>

          <div className="mb-6">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              You're about to save {selectedLeads.length} leads to your CRM. These leads will be marked with "AI Generated" as their source.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {selectedLeads.map((lead, index) => {
                const leadId = lead.id || `${lead.ContactName}-${lead.CompanyName}-${lead.ContactEmail}`;
                return (
                  <div key={lead.id || index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {lead.ContactName || 'Unknown Contact'}
                      </h4>
                      <div 
                        className={`relative px-2 py-1 rounded-full text-xs font-medium cursor-help ${getScoreBgColor(lead.aiScore || 75)} ${getScoreColor(lead.aiScore || 75)}`}
                        onMouseEnter={() => setHoveredLeadScore(leadId)}
                        onMouseLeave={() => setHoveredLeadScore(null)}
                      >
                        {lead.aiScore || 75}% Match
                        
                        {/* Tooltip */}
                        {hoveredLeadScore === leadId && (
                          <div className="absolute z-50 w-72 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl top-full right-0 mt-2">
                            <div className="space-y-2">
                              <div className="flex items-center justify-between pb-2 border-b border-gray-200 dark:border-gray-700">
                                <h5 className="font-semibold text-sm text-gray-900 dark:text-white">
                                  เหตุผลการให้คะแนน
                                </h5>
                                <span className={`text-lg font-bold ${getScoreColor(lead.aiScore || 75)}`}>
                                  {lead.aiScore || 75}%
                                </span>
                              </div>
                              
                              <div className="text-xs">
                                <p className="font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  {getScoreReasons(lead).summary}
                                </p>
                                
                                <div className="space-y-1">
                                  {getScoreReasons(lead).reasons.map((reason, idx) => (
                                    <div key={idx} className="flex items-start space-x-1">
                                      <span className="text-gray-600 dark:text-gray-400 leading-tight">
                                        {reason}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                                
                                <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                                  <p className="text-xs text-gray-500 dark:text-gray-500 italic">
                                    💡 คะแนนคำนวณจากความครบถ้วนและคุณภาพของข้อมูล
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {(lead.CompanyName || lead.ContactName || '')}{lead.JobTitle ? ` • ${lead.JobTitle}` : ''}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              onClick={() => setCurrentStep(2)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2"
            >
              <X className="h-4 w-4" />
              <span>Back to Review</span>
            </button>
            <button
              onClick={saveSelectedLeads}
              disabled={isGenerating}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
            >
              {isGenerating ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Save {selectedLeads.length} Leads</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Progress Bar for Generation */}
      {isGenerating && currentStep === 1 && (
        <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Loader className="h-5 w-5 animate-spin text-blue-600" />
            <h3 className="text-lg font-medium dark:text-white">AI is analyzing and searching for leads...</h3>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="mt-3 space-y-1">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {searchCriteria.region === 'th' 
                ? '🤖 AI กำลังวิเคราะห์เกณฑ์การค้นหาและกรองผลลัพธ์ให้ตรงกับความต้องการ...'
                : '🤖 AI is analyzing search criteria and filtering results to match your requirements...'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500">
              {searchCriteria.region === 'th'
                ? 'AI จะใช้เวลา 15-45 วินาที ในการวิเคราะห์และกรองผลลัพธ์ให้ตรงกับความต้องการ'
                : 'AI will take 15-45 seconds to analyze and filter results to match your requirements.'}
            </p>
          </div>
        </div>
      )}

      {/* Lead Detail Modal */}
      {showLeadModal && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Lead Details
                </h3>
                <button
                  onClick={() => {
                    setShowLeadModal(false);
                    setSelectedLead(null);
                  }}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Lead Information */}
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Contact Name
                    </label>
                    <p className="text-gray-900 dark:text-white">{selectedLead.ContactName || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Job Title
                    </label>
                    <p className="text-gray-900 dark:text-white">{selectedLead.JobTitle || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Company Name
                    </label>
                    <p className="text-gray-900 dark:text-white">{selectedLead.CompanyName || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Industry
                    </label>
                    <p className="text-gray-900 dark:text-white">{selectedLead.Industry || 'N/A'}</p>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                    <Phone className="h-5 w-5 mr-2" />
                    Contact Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Email
                      </label>
                      <p className="text-gray-900 dark:text-white">{selectedLead.ContactEmail || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Phone
                      </label>
                      <p className="text-gray-900 dark:text-white">{selectedLead.ContactPhone || 'N/A'}</p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Website
                      </label>
                      <p className="text-gray-900 dark:text-white">
                        {selectedLead.Website ? (
                          <a 
                            href={selectedLead.Website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                          >
                            {selectedLead.Website}
                          </a>
                        ) : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                    <Building className="h-5 w-5 mr-2" />
                    Additional Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Company Size
                      </label>
                      <p className="text-gray-900 dark:text-white">{selectedLead.CompanySize || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Location
                      </label>
                      <p className="text-gray-900 dark:text-white">{selectedLead.Location || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        AI Score
                      </label>
                      <p className="text-gray-900 dark:text-white">{selectedLead.aiScore || 75}%</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Source
                      </label>
                      <div className="flex items-center space-x-2">
                        {(() => {
                          const badge = getSourceBadge(selectedLead.source);
                          return (
                            <span 
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                badge.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' :
                                badge.color === 'indigo' ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200' :
                                badge.color === 'cyan' ? 'bg-cyan-100 dark:bg-cyan-900 text-cyan-800 dark:text-cyan-200' :
                                badge.color === 'green' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' :
                                badge.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200' :
                                badge.color === 'orange' ? 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200' :
                                'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                              }`}
                              title={badge.tooltip || badge.label}
                            >
                              <span className="mr-1">{badge.icon}</span>
                              <span>{badge.label}</span>
                            </span>
                          );
                        })()}
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          ({selectedLead.source || 'AI Generated'})
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {selectedLead.Description && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <div className="space-y-2">
                      <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg max-h-96 overflow-y-auto">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Original:</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">{selectedLead.Description}</p>
                      </div>
                      <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 max-h-96 overflow-y-auto">
                        <p className="text-xs text-green-600 dark:text-green-400 mb-1 flex items-center">
                          <Sparkles className="h-3 w-3 mr-1" />
                          Enhanced (will be saved):
                        </p>
                        <p className="text-sm text-gray-900 dark:text-white whitespace-pre-line">{enhanceContent(selectedLead.Description)}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedLead.Notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Notes
                    </label>
                    <div className="space-y-2">
                      <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg max-h-96 overflow-y-auto">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Original:</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">{selectedLead.Notes}</p>
                      </div>
                      <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 max-h-96 overflow-y-auto">
                        <p className="text-xs text-green-600 dark:text-green-400 mb-1 flex items-center">
                          <Sparkles className="h-3 w-3 mr-1" />
                          Enhanced (will be saved):
                        </p>
                        <p className="text-sm text-gray-900 dark:text-white whitespace-pre-line">{enhanceContent(selectedLead.Notes)}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Source URL */}
                {selectedLead.LinkedIn && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Source URL
                    </label>
                    <a 
                      href={selectedLead.LinkedIn} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 dark:text-blue-400 flex items-center"
                    >
                      <span>View Source URL</span>
                      <ExternalLink className="h-4 w-4 ml-1" />
                    </a>
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => {
                    setShowLeadModal(false);
                    setSelectedLead(null);
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSaveLead(selectedLead)}
                  disabled={isSaving}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  {isSaving ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span>Save Lead</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Customer Check Modal */}
      {showDuplicateModal && pendingLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                    <AlertCircle className="h-6 w-6 text-yellow-600 mr-2" />
                    Existing Customer Found / พบลูกค้าที่มีอยู่แล้ว
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    We found {duplicateCustomers.length} customer(s) with similar company name
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowDuplicateModal(false);
                    setPendingLead(null);
                    setDuplicateCustomers([]);
                    setSelectedCustomer(null);
                  }}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Lead Information */}
              <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">
                  New Lead Information:
                </h4>
                <div className="space-y-1 text-sm">
                  <p><span className="font-medium">Company:</span> {pendingLead.CompanyName}</p>
                  {pendingLead.ContactName && <p><span className="font-medium">Contact:</span> {pendingLead.ContactName}</p>}
                  {pendingLead.Industry && <p><span className="font-medium">Industry:</span> {pendingLead.Industry}</p>}
                </div>
              </div>

              {/* Matching Customers */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                  Select Existing Customer or Create New:
                </h4>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {duplicateCustomers.map((customer) => (
                    <label
                      key={customer.CustomerID}
                      className={`flex items-start space-x-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                        selectedCustomer === customer.CustomerID
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <input
                        type="radio"
                        name="customer"
                        checked={selectedCustomer === customer.CustomerID}
                        onChange={() => setSelectedCustomer(customer.CustomerID)}
                        className="mt-1 h-4 w-4 text-blue-600"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {customer.CompanyName}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 space-y-1">
                          {customer.Industry && (
                            <div className="flex items-center">
                              <Building className="h-3 w-3 mr-1" />
                              {customer.Industry}
                            </div>
                          )}
                          {customer.Phone && (
                            <div className="flex items-center">
                              <Phone className="h-3 w-3 mr-1" />
                              {customer.Phone}
                            </div>
                          )}
                          {customer.Email && (
                            <div className="flex items-center">
                              <Mail className="h-3 w-3 mr-1" />
                              {customer.Email}
                            </div>
                          )}
                          <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            Customer ID: {customer.CustomerID} • Created: {new Date(customer.CreatedAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={async () => {
                    // Save lead without linking to customer
                    setShowDuplicateModal(false);
                    await saveLeadAfterDuplicateCheck(pendingLead, null);
                    setPendingLead(null);
                    setDuplicateCustomers([]);
                    setSelectedCustomer(null);
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Create New Lead Only
                </button>
                <button
                  onClick={async () => {
                    if (!selectedCustomer) {
                      alert('Please select a customer to link to, or choose "Create New Lead Only"');
                      return;
                    }
                    // Save lead with customer link
                    setShowDuplicateModal(false);
                    await saveLeadAfterDuplicateCheck(pendingLead, selectedCustomer);
                    setPendingLead(null);
                    setDuplicateCustomers([]);
                    setSelectedCustomer(null);
                  }}
                  disabled={!selectedCustomer}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  <span>Link to Selected Customer</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadGen;
